import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Category } from '../../utils/categories';
import { saveCategoriesToServer } from '../../utils/CategoryAPI';
import './CategoryQuickAddModal.css';

interface CategoryQuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryAdded: (category: Category) => void;
  existingCategories: Category[];
}

const CategoryQuickAddModal: React.FC<CategoryQuickAddModalProps> = ({
  isOpen,
  onClose,
  onCategoryAdded,
  existingCategories
}) => {
  // Helper function to get CSS variable value
  const getCSSVariable = (variableName: string, fallback: string = '#8B4513'): string => {
    if (typeof window === 'undefined') return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(variableName).trim();
    return value || fallback;
  };

  const getDefaultColor = (): string => {
    return getCSSVariable('--color-primary', '#8B4513');
  };

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: getDefaultColor(),
  });

  // Update default color when theme changes
  useEffect(() => {
    if (!formData.color || formData.color === '#8B4513') {
      setFormData(prev => ({ ...prev, color: getDefaultColor() }));
    }
  }, []);
  const [isSaving, setIsSaving] = useState(false);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.slug.trim()) {
      alert('Category name is required');
      return;
    }

    // Check if slug already exists
    if (existingCategories.some(cat => cat.slug === formData.slug)) {
      alert('A category with this slug already exists. Please choose a different name.');
      return;
    }

    setIsSaving(true);

    try {
      const newCategory: Category = {
        id: Date.now().toString(),
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim(),
        color: formData.color,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        order: existingCategories.length + 1,
      };

      // Add to existing categories and save
      const updatedCategories = [...existingCategories, newCategory];
      const success = await saveCategoriesToServer(updatedCategories);

      if (success) {
        // Also save to localStorage as backup
        localStorage.setItem('admin_categories', JSON.stringify(updatedCategories));
        
        // Dispatch events to notify other components
        window.dispatchEvent(new CustomEvent('categories_updated'));
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'admin_categories',
          newValue: JSON.stringify(updatedCategories),
          oldValue: localStorage.getItem('admin_categories'),
          storageArea: localStorage
        }));

        onCategoryAdded(newCategory);
        resetForm();
        onClose();
      } else {
        alert('Failed to save category. Please try again.');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('An error occurred while saving the category.');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      color: '#8B4513',
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="category-quick-add-modal-overlay"
      onClick={handleClose}
    >
      <div 
        className="category-quick-add-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Add New Category</h3>
          <button className="close-button" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label htmlFor="category-name">Category Name *</label>
            <input
              type="text"
              id="category-name"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter category name"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="category-slug">Category Slug *</label>
            <input
              type="text"
              id="category-slug"
              value={formData.slug}
              onChange={(e) => setFormData({...formData, slug: e.target.value})}
              placeholder="category-slug"
              required
            />
            <small>Auto-generated from name, but you can edit it</small>
          </div>

          <div className="form-group">
            <label htmlFor="category-description">Description</label>
            <textarea
              id="category-description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              placeholder="Category description (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category-color">Category Color</label>
            <div className="color-picker-container">
              <input
                type="color"
                id="category-color"
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
              />
              <div className="color-presets">
                <div className="color-preset" style={{background: '#8B4513'}} onClick={() => setFormData({...formData, color: '#8B4513'})}></div>
                <div className="color-preset" style={{background: '#2c5aa0'}} onClick={() => setFormData({...formData, color: '#2c5aa0'})}></div>
                <div className="color-preset" style={{background: '#2d5016'}} onClick={() => setFormData({...formData, color: '#2d5016'})}></div>
                <div className="color-preset" style={{background: '#a0522d'}} onClick={() => setFormData({...formData, color: '#a0522d'})}></div>
                <div className="color-preset" style={{background: '#6a5acd'}} onClick={() => setFormData({...formData, color: '#6a5acd'})}></div>
                <div className="color-preset" style={{background: '#cd5c5c'}} onClick={() => setFormData({...formData, color: '#cd5c5c'})}></div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i>
                  Add Category
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default CategoryQuickAddModal;









